import { ApplicationsList } from './ApplicationsList';
import {
	resetApplicationsListCache,
	useApplicationsList,
} from './useApplicationsList';
import { useCallback, useEffect, useState } from 'react';
import {
	ApplicationSummary,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { useApplicationId } from '../../../hooks/useApplicationId';
import {
	SCREENING_DASHBOARD,
	INTERVIEW_LANDING,
	CENTRAL_TEAM_DASHBOARD,
	PROPOSER_LOAD_DASHBOARD,
	BATCH_HOME,
	PRE_WORK_APPLICATIONS,
} from '../../../shared/Routes';
import { Filter } from './Filter';
import { Button, Typography, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import { useBatchId } from '../../../hooks/useBatchId';
import { VerifyResultsModal } from './VerifyResultsModal';
import Icon from '../../../components/ui-components/Icon';
import DashboardIcon from '../../../assets/images/dashboard-icon.svg';
import LeftArrowWhite from '../../../assets/images/arrow-left-white.svg';
import { ApplicationStatus } from '../../../shared/Constants';
import { UnorderedListOutlined } from '@ant-design/icons';
import useUserSession from '../../../hooks/useUserSession';
import { ALL_ROLES, hasPrivilege } from '../../../shared/Utils';
import { PrivilegeCodes } from '../../../shared/RoleConstants';
import { NoAccess } from '../../../components/NoAccess';

const { Text } = Typography;
const { useBreakpoint } = Grid;

export const ApplicationsListContainer = () => {
	const {
		applicationsList,
		onPageChange,
		filter,
		onFilterChange,
		paging,
		selectedBatch,
	} = useApplicationsList({});
	const [openResultsModal, setOpenResultsModal] = useState(false);
	const [applicationToVerify, setApplicationToVerify] =
		useState<ApplicationSummary>();
	const { navigateWithApplicationId } = useApplicationId();
	const { navigateToRoute } = useBatchId();
	const { t, i18n } = useTranslation();
	const { selectedRole, loggedInUserPrivileges } = useUserSession();
	const screens = useBreakpoint();
	const { removeQueryParamFromUrl } = useBatchId();
	const isMobile = !screens.sm;
	const isLengthyLanguage = ['ta', 'ma'].includes(i18n.language);
	let btnClass = '';
	let mobileBtnClass = 'p-0';
	if (isLengthyLanguage) {
		btnClass = 'h-auto min-h-9 whitespace-normal break-words text-center';
		mobileBtnClass =
			'w-full mb-4 h-auto whitespace-normal break-words text-center';
	}

	useEffect(() => {
		removeQueryParamFromUrl();
	}, [removeQueryParamFromUrl]);

	const showScreeningDashboardBtn =
		selectedRole !== undefined &&
		selectedRole !== null &&
		ALL_ROLES.includes(selectedRole);

	const showScreeningDashboardPrivilege = hasPrivilege(
		PrivilegeCodes.GET_CANDIDATE_SCREENING_PROGRESS_SUMMARY,
		loggedInUserPrivileges
	);

	const showCentralTeamDashboard =
		selectedRole !== undefined &&
		selectedRole !== null &&
		ALL_ROLES.includes(selectedRole);

	const showCentralTeamDashboardPrivilege = hasPrivilege(
		PrivilegeCodes.GET_CENTRAL_TEAM_DASHBOARD_DETAIL,
		loggedInUserPrivileges
	);

	const showPreworkContent =
		selectedRole !== undefined &&
		selectedRole !== null &&
		[UserRole.APPROVER].includes(selectedRole);

	const showPreworkPrivilege = hasPrivilege(
		PrivilegeCodes.GET_PREWORK_APPLICATIONS,
		loggedInUserPrivileges
	);

	const showProposerLoad =
		selectedRole !== undefined &&
		selectedRole !== null &&
		ALL_ROLES.includes(selectedRole);

	const showProposerLoadPrivilege = hasPrivilege(
		PrivilegeCodes.GET_PROPOSER_LOAD_SUMMARY,
		loggedInUserPrivileges
	);

	const showEligibleCandidates = hasPrivilege(
		PrivilegeCodes.GET_ELIGIBLE_CANDIDATES_FOR_NEXT_BATCH,
		loggedInUserPrivileges
	);

	const showEvaluate = hasPrivilege(
		PrivilegeCodes.GET_APPLICATION,
		loggedInUserPrivileges
	);

	const showZonesProposerLoad = hasPrivilege(
		PrivilegeCodes.GET_ZONES_PROPOSER_LOAD_SUMMARY,
		loggedInUserPrivileges
	);

	const showViewCandidate = hasPrivilege(
		PrivilegeCodes.GET_CANDIDATES_LOAD_SUMMARY,
		loggedInUserPrivileges
	);

	const showViewScreeningDashboard = hasPrivilege(
		PrivilegeCodes.GET_CANDIDATE_SCREENING_PROGRESS_SUMMARY,
		loggedInUserPrivileges
	);

	const showProposerReassign = hasPrivilege(
		PrivilegeCodes.REASSIGN_FUNCTIONARY_TO_APPLICATION,
		loggedInUserPrivileges
	);

	const showUpdateRegion = hasPrivilege(
		PrivilegeCodes.UPDATE_REGION_ZONE_CENTER_OF_APPLICATION,
		loggedInUserPrivileges
	);

	const showEvaluatePreworkApplication = hasPrivilege(
		PrivilegeCodes.CAPTURE_PREWORK_MENTOR_ANSWERS,
		loggedInUserPrivileges
	);

	const showNoAccess =
		showEligibleCandidates ||
		showEvaluate ||
		showProposerLoadPrivilege ||
		showZonesProposerLoad ||
		showViewCandidate ||
		showViewScreeningDashboard ||
		showProposerReassign ||
		showUpdateRegion ||
		showCentralTeamDashboardPrivilege ||
		showPreworkPrivilege ||
		showEvaluatePreworkApplication;

	const handleEvaluateClick = useCallback(
		(application: ApplicationSummary) => {
			navigateWithApplicationId(INTERVIEW_LANDING, application.applicationId);
		},
		[navigateWithApplicationId]
	);

	const handleVerifyResultsClick = useCallback(
		(application: ApplicationSummary) => {
			setApplicationToVerify(application);
			setOpenResultsModal(true);
		},
		[]
	);

	const handleScreeningDashboardClick = useCallback(() => {
		navigateToRoute(SCREENING_DASHBOARD);
	}, [navigateToRoute]);

	const handleCentralTeamDashboardClick = useCallback(() => {
		resetApplicationsListCache();
		navigateToRoute(CENTRAL_TEAM_DASHBOARD);
	}, [navigateToRoute]);

	const handleProposerLoadDashboard = useCallback(() => {
		navigateToRoute(PROPOSER_LOAD_DASHBOARD);
	}, [navigateToRoute]);

	const handleGoToPreworkCandidates = useCallback(() => {
		navigateToRoute(PRE_WORK_APPLICATIONS);
	}, [navigateToRoute]);

	const filterComponent = filter && (
		<div className="mb-[16px]">
			<Filter value={filter} onFilterChange={onFilterChange} />
		</div>
	);

	const dashboardButtons = (
		<div className="flex gap-2">
			{showProposerLoad && showProposerLoadPrivilege && (
				<Button
					className="flex-1 h-9 text-primary-blue font-sans font-bold"
					onClick={handleProposerLoadDashboard}
					icon={<Icon src={DashboardIcon} />}
					block={isMobile}
				>
					{t('proposerLoad')}
				</Button>
			)}
			{showScreeningDashboardBtn && showScreeningDashboardPrivilege && (
				<Button
					className={`flex-1 h-9 text-primary-blue font-sans font-bold ${btnClass}`}
					onClick={handleScreeningDashboardClick}
					icon={<Icon src={DashboardIcon} />}
					block={isMobile}
					data-testid="screening-dashboard-btn"
				>
					{t('adminDashboard')}
				</Button>
			)}
			{showCentralTeamDashboard && showCentralTeamDashboardPrivilege && (
				<Button
					className="flex-1 h-9 text-primary-blue font-sans font-bold"
					onClick={handleCentralTeamDashboardClick}
					icon={<Icon src={DashboardIcon} />}
					block={isMobile}
				>
					{t('centralTeamDashboard')}
				</Button>
			)}
			{showPreworkContent && showPreworkPrivilege && (
				<Button
					className={`flex-1 h-9 text-primary-blue font-sans font-bold ${btnClass}`}
					onClick={handleGoToPreworkCandidates}
					icon={<UnorderedListOutlined />}
					block={isMobile}
				>
					{t('preworkApproverReview')}
				</Button>
			)}
		</div>
	);

	const handleBackClick = useCallback(() => {
		sessionStorage.removeItem('SELECTED_BATCH');
		const defaultFilters = { status: [ApplicationStatus.FUNCTIONARY_ASSIGNED] };
		onFilterChange(defaultFilters);
		navigateToRoute(BATCH_HOME);
	}, [onFilterChange, navigateToRoute]);

	if (!showNoAccess) {
		return <NoAccess />;
	}

	return (
		<div className="flex flex-col p-[16px]">
			<div className="flex gap-8">
				<div className="flex gap-6">
					{selectedRole === UserRole.SNP_ADMIN ? (
						<>
							<Button
								type="primary"
								className="hidden md:block font-bold text-sm"
								onClick={handleBackClick}
							>
								<span className="flex gap-2">
									<Icon src={LeftArrowWhite} width="w-4" height="h-4" />
									{t('back')}
								</span>
							</Button>
							<Text className="hidden md:block font-sans font-bold text-xl text-secondary-black mb-4 whitespace-nowrap">
								{t('listOfCandidates')} - {selectedBatch?.name}
							</Text>
						</>
					) : (
						<Text className="hidden md:block font-sans font-bold text-xl text-secondary-black mb-4 whitespace-nowrap">
							{showPreworkContent
								? t('listOfScreeningCandidates')
								: t('listOfCandidates')}
						</Text>
					)}
				</div>

				<div className="flex flex-col w-full">
					{showScreeningDashboardBtn && isMobile && (
						<Button
							onClick={handleScreeningDashboardClick}
							className={`text-center ${
								isMobile
									? 'w-full mb-4 text-primary-blue font-bold'
									: 'underline hover:underline-offset-4 text-primary-blue font-bold p-0'
							}`}
							block={isMobile}
						>
							{t('adminDashboard')}
						</Button>
					)}
					{showCentralTeamDashboard && isMobile && (
						<Button
							onClick={handleCentralTeamDashboardClick}
							className={`text-center ${
								isMobile
									? 'w-full mb-4 text-primary-blue font-bold'
									: 'underline hover:underline-offset-4 text-primary-blue font-bold p-0'
							}`}
							block={isMobile}
						>
							{t('centralTeamDashboard')}
						</Button>
					)}
					{showPreworkContent && isMobile && (
						<Button
							onClick={handleGoToPreworkCandidates}
							className={`text-center text-primary-blue font-bold ${mobileBtnClass}`}
							block={isMobile}
						>
							{t('preworkApproverReview')}
						</Button>
					)}
				</div>
			</div>
			{isMobile ? (
				<>{filterComponent}</>
			) : (
				<div className="flex items-center justify-between">
					{filterComponent}
					{dashboardButtons}
				</div>
			)}

			<ApplicationsList
				data={applicationsList}
				onPageChange={onPageChange}
				onEvaluateClick={handleEvaluateClick}
				onVerifyResultsClick={handleVerifyResultsClick}
				pageConfig={paging}
			/>

			<VerifyResultsModal
				openModal={openResultsModal}
				onClose={() => setOpenResultsModal(false)}
				application={applicationToVerify}
			/>
		</div>
	);
};
